import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../ui/Card';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Switch } from '../ui/Switch';
import { SELECT_CLASS } from '../AudiencePicker';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { ApiError } from '../../types/api';
import {
  AI_LANGUAGE_LIST,
  type AiLanguage,
  type AiPrompt,
  type AiTask,
  type AiTaskInput,
  type AiTaskKind,
  type AiTaskRun,
  type AiTasksResponse,
} from '../../types/ai';

const TEXTAREA_CLASS =
  'min-h-[72px] w-full rounded-[9px] border border-zinc-300 px-3 py-2 text-sm bg-card text-text';

const KIND_LABELS: Record<AiTaskKind, string> = {
  prompt: 'Your own prompt',
  library_prompt: 'A saved prompt',
  subject_lines: 'Subject lines',
  newsletter: 'Newsletter draft',
  segment: 'Segment suggestion',
};

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface Draft {
  name: string;
  kind: AiTaskKind;
  text: string;
  promptId: string;
  topic: string;
  languages: AiLanguage[];
  bullets: string;
  language: AiLanguage;
  description: string;
  frequency: 'daily' | 'weekly';
  weekday: number;
  hour: number;
}

const EMPTY: Draft = {
  name: '',
  kind: 'prompt',
  text: '',
  promptId: '',
  topic: '',
  languages: ['en'],
  bullets: '',
  language: 'en',
  description: '',
  frequency: 'weekly',
  weekday: 1,
  hour: 9,
};

function draftOf(t: AiTask): Draft {
  const d: Draft = {
    ...EMPTY,
    name: t.name,
    kind: t.input.kind,
    frequency: t.frequency,
    weekday: t.weekday ?? 1,
    hour: t.hour,
  };
  const i = t.input;
  if (i.kind === 'prompt') d.text = i.text;
  if (i.kind === 'library_prompt') d.promptId = i.promptId;
  if (i.kind === 'subject_lines') {
    d.topic = i.topic;
    d.languages = i.languages;
  }
  if (i.kind === 'newsletter') {
    d.bullets = i.bullets;
    d.language = i.language;
  }
  if (i.kind === 'segment') d.description = i.description;
  return d;
}

function inputOf(d: Draft): AiTaskInput {
  const base = {
    name: d.name.trim(),
    frequency: d.frequency,
    weekday: d.frequency === 'weekly' ? d.weekday : null,
    hour: d.hour,
  };
  switch (d.kind) {
    case 'prompt':
      return { ...base, input: { kind: 'prompt', text: d.text } };
    case 'library_prompt':
      return { ...base, input: { kind: 'library_prompt', promptId: d.promptId } };
    case 'subject_lines':
      return {
        ...base,
        input: { kind: 'subject_lines', topic: d.topic, languages: d.languages, count: 3 },
      };
    case 'newsletter':
      return { ...base, input: { kind: 'newsletter', bullets: d.bullets, language: d.language } };
    case 'segment':
      return { ...base, input: { kind: 'segment', description: d.description } };
  }
}

function draftReady(d: Draft): boolean {
  if (d.name.trim() === '') return false;
  switch (d.kind) {
    case 'prompt':
      return d.text.trim() !== '';
    case 'library_prompt':
      return d.promptId !== '';
    case 'subject_lines':
      return d.topic.trim() !== '' && d.languages.length > 0;
    case 'newsletter':
      return d.bullets.trim() !== '';
    case 'segment':
      return d.description.trim() !== '';
  }
}

function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return err.body.message ?? 'You do not have access to do that.';
    if (err.body.message) return err.body.message;
  }
  return fallback;
}

function formatTime(ms: number | null): string {
  if (ms === null) return '-';
  return `${new Date(ms).toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

const STATUS_LABEL: Record<AiTaskRun['status'], string> = {
  ok: 'Done',
  error: 'Failed',
  skipped: 'Skipped',
  running: 'Running',
};

function RunOutput({ run }: { run: AiTaskRun }) {
  const out = run.output as Record<string, unknown> | null;
  if (!out) return null;
  if (typeof out.text === 'string') {
    return <p className="m-0 whitespace-pre-wrap text-sm">{out.text}</p>;
  }
  if (Array.isArray(out.suggestions)) {
    return (
      <ul className="m-0 flex flex-col gap-1 pl-4 text-sm">
        {(out.suggestions as { language: string; subject: string; preheader: string }[]).map(
          (s, i) => (
            <li key={i}>
              <span className="font-medium">{s.subject}</span>
              <span className="text-muted">
                {' '}
                · {s.preheader} ({s.language})
              </span>
            </li>
          ),
        )}
      </ul>
    );
  }
  if (out.template && typeof out.template === 'object') {
    const t = out.template as { id: string; name: string };
    return (
      <p className="m-0 text-sm">
        Draft template saved:{' '}
        <Link className="text-link underline" to={`/skabeloner/${t.id}`}>
          {t.name}
        </Link>
        . Subject: {String(out.subject ?? '')}. Review it before you use it in a campaign.
      </p>
    );
  }
  if (typeof out.name === 'string') {
    return (
      <p className="m-0 text-sm">
        Suggested segment: <span className="font-medium">{out.name}</span>. It was not saved. Build
        it in Segments if you want to keep it.
      </p>
    );
  }
  return null;
}

function RunHistory({ taskId }: { taskId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ai-tasks', taskId, 'runs'],
    queryFn: () => apiFetch<{ runs: AiTaskRun[] }>(`/ai/tasks/${taskId}/runs`),
  });
  if (isLoading) return <p className="m-0 text-xs text-muted">Loading history…</p>;
  if (isError || !data) return <Alert>Could not load the history.</Alert>;
  if (data.runs.length === 0) return <p className="m-0 text-xs text-muted">No runs yet.</p>;
  return (
    <ol className="m-0 flex list-none flex-col gap-2 p-0" aria-label="Run history">
      {data.runs.map((r) => (
        <li key={r.id} className="flex flex-col gap-1 rounded-[10px] border border-border p-3">
          <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted">
            <span className="font-semibold text-text">{STATUS_LABEL[r.status]}</span>
            <span>{formatTime(r.startedAt)}</span>
            <span>{r.source === 'manual' ? 'Run by hand' : 'On schedule'}</span>
          </div>
          {r.error ? <p className="m-0 text-xs text-danger-text">{r.error}</p> : null}
          <RunOutput run={r} />
        </li>
      ))}
    </ol>
  );
}

function TaskForm({
  initial,
  prompts,
  pending,
  error,
  onSave,
  onCancel,
}: {
  initial: Draft;
  prompts: AiPrompt[];
  pending: boolean;
  error: string | null;
  onSave: (input: AiTaskInput) => void;
  onCancel: () => void;
}) {
  const [d, setD] = useState<Draft>(initial);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((p) => ({ ...p, [k]: v }));

  return (
    <form
      className="flex flex-col gap-3 rounded-[10px] border border-border p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (draftReady(d)) onSave(inputOf(d));
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="ai-task-name">Task name</Label>
        <Input
          id="ai-task-name"
          maxLength={80}
          value={d.name}
          onChange={(e) => set('name', e.target.value)}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="ai-task-kind">What the task does</Label>
        <select
          id="ai-task-kind"
          className={SELECT_CLASS}
          value={d.kind}
          onChange={(e) => set('kind', e.target.value as AiTaskKind)}
        >
          {(Object.keys(KIND_LABELS) as AiTaskKind[]).map((k) => (
            <option key={k} value={k}>
              {KIND_LABELS[k]}
            </option>
          ))}
        </select>
      </div>

      {d.kind === 'prompt' ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ai-task-text">Prompt</Label>
          <textarea
            id="ai-task-text"
            className={TEXTAREA_CLASS}
            maxLength={2000}
            value={d.text}
            onChange={(e) => set('text', e.target.value)}
          />
        </div>
      ) : null}
      {d.kind === 'library_prompt' ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ai-task-prompt">Saved prompt</Label>
          <select
            id="ai-task-prompt"
            className={SELECT_CLASS}
            value={d.promptId}
            onChange={(e) => set('promptId', e.target.value)}
          >
            <option value="">Pick a prompt</option>
            {prompts.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
          {prompts.length === 0 ? (
            <p className="m-0 text-xs text-muted">Save a prompt in the prompt library first.</p>
          ) : null}
        </div>
      ) : null}
      {d.kind === 'subject_lines' ? (
        <>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ai-task-topic">What the mail is about</Label>
            <textarea
              id="ai-task-topic"
              className={TEXTAREA_CLASS}
              maxLength={2000}
              value={d.topic}
              onChange={(e) => set('topic', e.target.value)}
            />
          </div>
          <fieldset className="m-0 flex flex-wrap gap-3 border-0 p-0">
            <legend className="mb-1 text-sm font-medium">Languages</legend>
            {AI_LANGUAGE_LIST.map((l) => (
              <label key={l.value} className="flex items-center gap-1.5 text-sm">
                <input
                  type="checkbox"
                  checked={d.languages.includes(l.value)}
                  onChange={() =>
                    set(
                      'languages',
                      d.languages.includes(l.value)
                        ? d.languages.filter((x) => x !== l.value)
                        : [...d.languages, l.value],
                    )
                  }
                />
                {l.label}
              </label>
            ))}
          </fieldset>
        </>
      ) : null}
      {d.kind === 'newsletter' ? (
        <>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ai-task-bullets">Points for the newsletter</Label>
            <textarea
              id="ai-task-bullets"
              className={TEXTAREA_CLASS}
              maxLength={2000}
              value={d.bullets}
              onChange={(e) => set('bullets', e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ai-task-language">Language</Label>
            <select
              id="ai-task-language"
              className={SELECT_CLASS}
              value={d.language}
              onChange={(e) => set('language', e.target.value as AiLanguage)}
            >
              {AI_LANGUAGE_LIST.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
          </div>
        </>
      ) : null}
      {d.kind === 'segment' ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ai-task-description">Who should be in the segment</Label>
          <textarea
            id="ai-task-description"
            className={TEXTAREA_CLASS}
            maxLength={2000}
            value={d.description}
            onChange={(e) => set('description', e.target.value)}
          />
        </div>
      ) : null}

      <div className="grid grid-cols-3 gap-2 max-sm:grid-cols-1">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ai-task-frequency">How often</Label>
          <select
            id="ai-task-frequency"
            className={SELECT_CLASS}
            value={d.frequency}
            onChange={(e) => set('frequency', e.target.value as Draft['frequency'])}
          >
            <option value="weekly">Every week</option>
            <option value="daily">Every day</option>
          </select>
        </div>
        {d.frequency === 'weekly' ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ai-task-weekday">Day (UTC)</Label>
            <select
              id="ai-task-weekday"
              className={SELECT_CLASS}
              value={d.weekday}
              onChange={(e) => set('weekday', Number(e.target.value))}
            >
              {WEEKDAYS.map((w, i) => (
                <option key={w} value={i}>
                  {w}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ai-task-hour">Time (UTC)</Label>
          <select
            id="ai-task-hour"
            className={SELECT_CLASS}
            value={d.hour}
            onChange={(e) => set('hour', Number(e.target.value))}
          >
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h}>
                {`${String(h).padStart(2, '0')}:00`}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error ? <Alert>{error}</Alert> : null}
      <div className="flex flex-wrap justify-end gap-2 max-sm:flex-col-reverse">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!draftReady(d) || pending}>
          {pending ? 'Saving…' : 'Save task'}
        </Button>
      </div>
    </form>
  );
}

/**
 * K7: prompts or skills on a weekly or daily UTC schedule. A task is created
 * off; only an admin turns it on or runs it by hand. A run only stores a
 * result (or a draft template) for review.
 */
export function AiScheduledTasks() {
  const { isAdmin, isEditor } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<AiTask | 'new' | null>(null);
  const [history, setHistory] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['ai-tasks'],
    queryFn: () => apiFetch<AiTasksResponse>('/ai/tasks'),
  });
  const prompts = useQuery({
    queryKey: ['ai-prompts'],
    queryFn: () => apiFetch<{ prompts: AiPrompt[] }>('/ai/prompts'),
    enabled: editing !== null,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['ai-tasks'] });

  const save = useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: AiTaskInput }) =>
      id
        ? apiFetch<{ task: AiTask }>(`/ai/tasks/${id}`, { method: 'PUT', body: input })
        : apiFetch<{ task: AiTask }>('/ai/tasks', { method: 'POST', body: input }),
    onSuccess: () => {
      setEditing(null);
      void refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: (t: AiTask) =>
      t.enabled
        ? apiFetch<{ task: AiTask }>(`/ai/tasks/${t.id}/disable`, { method: 'POST' })
        : apiFetch<{ task: AiTask }>(`/ai/tasks/${t.id}/enable`, { method: 'POST' }),
    onSuccess: () => {
      setActionError(null);
      void refresh();
    },
    onError: (err) => setActionError(errorText(err, 'Could not change the task. Try again.')),
  });
  const run = useMutation({
    mutationFn: (t: AiTask) =>
      apiFetch<{ run: AiTaskRun | null }>(`/ai/tasks/${t.id}/run`, { method: 'POST' }),
    onSuccess: (_res, t) => {
      setActionError(null);
      setHistory(t.id);
      void refresh();
    },
    onError: (err) => setActionError(errorText(err, 'Could not run the task. Try again.')),
  });
  const remove = useMutation({
    mutationFn: (t: AiTask) => apiFetch<{ ok: true }>(`/ai/tasks/${t.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setActionError(null);
      void refresh();
    },
    onError: (err) => setActionError(errorText(err, 'Could not delete the task. Try again.')),
  });

  return (
    <Card className="p-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold m-0">Scheduled tasks</h2>
        {isEditor && editing === null ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              save.reset();
              setEditing('new');
            }}
          >
            New task
          </Button>
        ) : null}
      </div>
      <p className="m-0 text-xs text-muted">
        Run a prompt or a skill every week or every day. A new task is off until an admin turns it
        on. A run only saves a result or a draft for you to review. It never creates, approves,
        schedules or sends a campaign.
      </p>

      {isLoading ? <p className="m-0 text-sm text-muted">Loading tasks…</p> : null}
      {isError ? <Alert>Could not load the scheduled tasks.</Alert> : null}

      {data ? (
        <>
          <p className="m-0 text-sm">
            {data.usage.runs} of {data.usage.cap} task runs today (UTC).
          </p>
          {!data.aiEnabled ? (
            <Alert>The AI assistant is off, so no task runs. An admin can turn it on above.</Alert>
          ) : !data.available ? (
            <Alert>The AI assistant is not available in this environment, so no task runs.</Alert>
          ) : null}
          {actionError ? <Alert>{actionError}</Alert> : null}

          {editing !== null ? (
            <TaskForm
              key={editing === 'new' ? 'new' : editing.id}
              initial={editing === 'new' ? EMPTY : draftOf(editing)}
              prompts={prompts.data?.prompts ?? []}
              pending={save.isPending}
              error={
                save.isError ? errorText(save.error, 'Could not save the task. Try again.') : null
              }
              onSave={(input) => save.mutate({ id: editing === 'new' ? null : editing.id, input })}
              onCancel={() => setEditing(null)}
            />
          ) : null}

          {data.tasks.length === 0 && editing === null ? (
            <p className="m-0 text-sm text-muted">No scheduled tasks yet.</p>
          ) : null}
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {data.tasks.map((t) => {
              const canChange = isAdmin || (isEditor && !t.enabled);
              return (
                <li
                  key={t.id}
                  className="flex flex-col gap-2 rounded-[10px] border border-border p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium break-words">{t.name}</div>
                      <div className="text-xs text-muted">
                        {KIND_LABELS[t.kind]} · {t.scheduleLabel}
                      </div>
                      <div className="text-xs text-muted">
                        {t.enabled ? `Next run ${formatTime(t.nextDueAt)}` : 'Off'}
                        {t.lastRunAt !== null
                          ? ` · Last run ${formatTime(t.lastRunAt)} (${STATUS_LABEL[t.lastStatus ?? 'running']})`
                          : ''}
                      </div>
                    </div>
                    <Switch
                      checked={t.enabled}
                      disabled={!isAdmin || toggle.isPending}
                      label={`Run ${t.name} on schedule`}
                      onChange={() => toggle.mutate(t)}
                    />
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {isAdmin ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        aria-label={`Run ${t.name} now`}
                        disabled={run.isPending}
                        onClick={() => run.mutate(t)}
                      >
                        {run.isPending && run.variables?.id === t.id ? 'Running…' : 'Run now'}
                      </Button>
                    ) : null}
                    {canChange ? (
                      <>
                        <Button
                          size="sm"
                          variant="secondary"
                          aria-label={`Edit ${t.name}`}
                          onClick={() => {
                            save.reset();
                            setEditing(t);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`Delete ${t.name}`}
                          disabled={remove.isPending}
                          onClick={() => {
                            if (window.confirm(`Delete the task "${t.name}" and its history?`)) {
                              remove.mutate(t);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </>
                    ) : null}
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-expanded={history === t.id}
                      aria-label={`History for ${t.name}`}
                      onClick={() => setHistory(history === t.id ? null : t.id)}
                    >
                      {history === t.id ? 'Hide history' : 'History'}
                    </Button>
                  </div>
                  {history === t.id ? <RunHistory taskId={t.id} /> : null}
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
    </Card>
  );
}
